import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { PostgrestError } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

function isValidSupabaseUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (url.includes("your_") || url.includes("placeholder")) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname.endsWith(".supabase.co");
  } catch {
    return false;
  }
}

if (!isValidSupabaseUrl(supabaseUrl) || !supabaseAnonKey) {
  throw new Error(
    "Missing or invalid Supabase environment variables. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
  );
}
// Strict singleton pattern
const STORAGE_KEY = 'surdurulebilirlik-auth-v12';
const CLIENT_KEY = '__supabase_singleton__';

// Global singleton instance
let _instance: SupabaseClient | null = null;

const createSupabaseClient = (): SupabaseClient => {
  // Return existing instance if available
  if (_instance) return _instance;
  
  // Check global window object
  if (typeof window !== 'undefined' && (window as any)[CLIENT_KEY]) {
    _instance = (window as any)[CLIENT_KEY];
    return _instance!;
  }
  
  // Suppress GoTrueClient multiple instance warning
  const originalWarn = console.warn;
  console.warn = (...args) => {
    if (args[0]?.includes?.('Multiple GoTrueClient')) return;
    originalWarn.apply(console, args);
  };
  
  // Create new instance only if none exists
  _instance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      storageKey: STORAGE_KEY,
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'X-Client-Info': 'surdurulebilirlik-app'
      }
    }
  });
  
  // Restore console.warn
  console.warn = originalWarn;
  
  // Store in global scope
  if (typeof window !== 'undefined') {
    (window as any)[CLIENT_KEY] = _instance;
  }
  
  return _instance!;
};

export const supabase = createSupabaseClient();

export const handleSupabaseError = (error: PostgrestError | Error | null, context?: string) => {
  if (!error) return;
  console.error(`Supabase Error${context ? ` (${context})` : ''}:`, error);
};

export type { PostgrestError };
